import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Paper,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import {
  Refresh,
  OpenInNew,
  Fullscreen,
  FullscreenExit,
} from "@mui/icons-material";
import BackBtn from "../../widgets/BackBtn";

const SOCIAL_WALL_URL = "https://social-wall.expoplanner.in/#/admin/login";

const SocialWallPage = () => {
  const [loading, setLoading] = useState(true);
  const [iframeKey, setIframeKey] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const handleRefresh = () => {
    setLoading(true);
    setIframeKey((prev) => prev + 1);
  };

  const handleOpenExternal = () => {
    window.open(SOCIAL_WALL_URL, "_blank");
  };

  const toggleFullscreen = () => {
    setIsFullscreen((prev) => !prev);
  };

  return (
    <Box
      sx={{
        pb: 4,
        height: isFullscreen ? "100vh" : "auto",
        position: isFullscreen ? "fixed" : "relative",
        top: isFullscreen ? 0 : "auto",
        left: isFullscreen ? 0 : "auto",
        right: isFullscreen ? 0 : "auto",
        bottom: isFullscreen ? 0 : "auto",
        zIndex: isFullscreen ? 1300 : 1,
        bgcolor: "#F8FAFC",
        p: isFullscreen ? 2 : 0,
      }}
    >
      {!isFullscreen && <BackBtn />}

      {/* Header Section */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: isFullscreen ? 1.5 : 3,
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{ fontWeight: 800, color: "#0F172A", letterSpacing: "-0.02em" }}
          >
            Social Wall Admin Portal
          </Typography>
          <Typography variant="body2" sx={{ color: "#64748B", mt: 0.5 }}>
            Live event social feed, post approvals, and social wall management.
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={handleRefresh}
            sx={{
              borderRadius: "8px",
              borderColor: "#E2E8F0",
              color: "#475467",
              textTransform: "none",
              fontWeight: 600,
              bgcolor: "#FFFFFF",
              "&:hover": { bgcolor: "#F8FAFC", borderColor: "#CBD5E1" },
            }}
          >
            Refresh Feed
          </Button>

          <Button
            variant="outlined"
            startIcon={<OpenInNew />}
            onClick={handleOpenExternal}
            sx={{
              borderRadius: "8px",
              borderColor: "#E2E8F0",
              color: "#2563EB",
              textTransform: "none",
              fontWeight: 600,
              bgcolor: "#FFFFFF",
              "&:hover": { bgcolor: "#EFF6FF", borderColor: "#BFDBFE" },
            }}
          >
            Open in New Tab
          </Button>

          <Tooltip title={isFullscreen ? "Exit Fullscreen" : "Fullscreen View"}>
            <IconButton
              onClick={toggleFullscreen}
              sx={{
                bgcolor: "#FFFFFF",
                border: "1px solid #E2E8F0",
                borderRadius: "8px",
                color: "#475467",
                "&:hover": { bgcolor: "#F8FAFC" },
              }}
            >
              {isFullscreen ? <FullscreenExit /> : <Fullscreen />}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Embedded Iframe Container */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: "14px",
          border: "1px solid #E2E8F0",
          boxShadow: "0 4px 16px rgba(15, 23, 42, 0.05)",
          overflow: "hidden",
          position: "relative",
          height: isFullscreen ? "calc(100vh - 100px)" : "calc(100vh - 170px)",
          minHeight: "650px",
          bgcolor: "#FFFFFF",
        }}
      >
        {loading && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "#FFFFFF",
              zIndex: 2,
              gap: 2,
            }}
          >
            <CircularProgress size={40} sx={{ color: "#2563EB" }} />
            <Typography variant="body2" color="#64748B" fontWeight="500">
              Loading Social Wall Admin Portal...
            </Typography>
          </Box>
        )}

        <iframe
          key={iframeKey}
          src={SOCIAL_WALL_URL}
          title="Social Wall Admin Portal"
          width="100%"
          height="100%"
          style={{
            border: "none",
            width: "100%",
            height: "100%",
            display: "block",
          }}
          onLoad={() => setLoading(false)}
          allow="fullscreen; camera; microphone; clipboard-write; autoplay"
        />
      </Paper>
    </Box>
  );
};

export default SocialWallPage;
